import React, {
   forwardRef,
   useEffect,
   useImperativeHandle,
   useRef,
   useState,
} from 'react';

(function polyfillGetUserMedia() {
   if (typeof window === 'undefined') {
      return;
   }

   if (navigator.mediaDevices === undefined) {
      (navigator as any).mediaDevices = {};
   }

   if (navigator.mediaDevices.getUserMedia === undefined) {
      navigator.mediaDevices.getUserMedia = function (constraints) {
         const getUserMedia =
            navigator.mediaDevices.getUserMedia ||
            navigator.webkitGetUserMedia ||
            navigator.mozGetUserMedia ||
            navigator.msGetUserMedia;
         if (!getUserMedia) {
            return Promise.reject(
               new Error('getUserMedia is not implemented in this browser')
            );
         }

         return new Promise(function (resolve, reject) {
            getUserMedia.call(navigator, constraints);
         });
      };
   }
})();

function hasGetUserMedia() {
   return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
}

export type WebcamProps = Omit<React.HTMLProps<HTMLVideoElement>, 'ref'> & {
   audio?: boolean;
   audioConstraints?: MediaStreamConstraints['audio'];
   disablePictureInPicture?: boolean;
   forceScreenshotSourceSize?: boolean;
   imageSmoothing?: boolean;
   mirrored?: boolean;
   minScreenshotHeight?: number;
   minScreenshotWidth?: number;
   onUserMedia?: (stream: MediaStream) => void;
   onUserMediaError?: (error: string | DOMException) => void;
   screenshotFormat?: 'image/webp' | 'image/png' | 'image/jpeg';
   screenshotQuality?: number;
   videoConstraints?: MediaStreamConstraints;
   closeVideoModalWithError?: Function;
};

export interface ChildComponentHandles {
   getMediaStream?: () => MediaStream | null;
   getVideoElement?: () => HTMLVideoElement | null;
}

export const VideoCam = forwardRef<ChildComponentHandles, WebcamProps>((props, ref) => {
   const {
      audio = false,
      disablePictureInPicture = false,
      mirrored = true,
      onUserMedia = () => undefined,
      onUserMediaError = () => undefined,
      minScreenshotWidth,
      minScreenshotHeight,
      audioConstraints,
      videoConstraints = {
         video: { facingMode: 'user' },
         audio: true
      },
      style = {},
      children,
      closeVideoModalWithError,
      ...rest
   } = props;
   const localRef = useRef<HTMLVideoElement | null>(null);
   const [hasUserMedia, setHasUserMedia] = useState<boolean | null>(null);
   const videoStream = useRef<MediaStream | null>(null);

   useEffect(() => {
      if (!hasGetUserMedia()) {
         onUserMediaError('getUserMedia not supported');
         return;
      }
      if (!hasUserMedia) {
         requestUserMedia();
      }

      return () => {
         stopMediaStream();
      };
   }, []);

   useImperativeHandle(ref, () => ({
      getMediaStream: () => {
         return videoStream.current;
      },
   }));

   const stopMediaStream = () => {
      const stream = videoStream.current;
      if (stream) {
         stream.getVideoTracks().forEach((track) => {
            stream.removeTrack(track);
            track.stop();
         });
         stream.getAudioTracks().forEach((track) => {
            stream.removeTrack(track);
            track.stop();
         });
      }
   };

   const handleUserMedia = (err: Error | null) => {
      if (err || !videoStream.current) {
         setHasUserMedia(false);
         onUserMediaError(err?.message || 'Failed to get user media');
         closeVideoModalWithError?.();
         return;
      }

      try {
         if (localRef.current) {
            localRef.current.srcObject = videoStream.current;
         }
         setHasUserMedia(true);
      } catch (error) {
         closeVideoModalWithError?.();
      }

      onUserMedia(videoStream.current);
   };

   const requestUserMedia = async () => {
      if ('MediaRecorder' in window) {
         try {
            videoStream.current = await navigator?.mediaDevices?.getUserMedia(
               videoConstraints as MediaStreamConstraints
            );
            handleUserMedia(null);
         } catch (error) {
            handleUserMedia(error as Error);
         }
      } else {
         closeVideoModalWithError?.();
      }
   };

   const videoStyle = mirrored
      ? { ...style, transform: `${style.transform || ''} scaleX(-1)` }
      : style;

   return (
      <video
         autoPlay
         disablePictureInPicture={disablePictureInPicture}
         controls={false}
         muted={!audio}
         playsInline
         ref={localRef}
         style={videoStyle}
         {...rest}
      />
   );
});
